import React, { useState } from 'react';
import { Linkedin, RefreshCw, HelpCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { LinkedInSetupModal } from './LinkedInSetupModal';
import { getUserApiCredentials } from '../lib/apiCredentials';
import { createOAuthState } from '../lib/oauthState';

interface LinkedInConnectProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  hasConnectedAccounts?: boolean;
}

interface LinkedInProfile {
  id: string;
  name: string;
  type: 'person' | 'organization';
}

export function LinkedInConnect({ onSuccess, onError, hasConnectedAccounts }: LinkedInConnectProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [profiles, setProfiles] = useState<LinkedInProfile[]>([]);
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [showSetupModal, setShowSetupModal] = useState(false);

  const handleLinkedInLogin = async () => {
    setIsLoading(true);
    try {
      // Get user's custom credentials or use defaults
      const credentials = await getUserApiCredentials();
      const clientId = credentials.linkedin_client_id;

      if (!clientId) {
        toast.error('LinkedIn Client ID not configured. Please add your credentials in API Settings.');
        setIsLoading(false);
        return;
      }

      const redirectUri = `${window.location.origin}/linkedin-callback`;
      // OpenID Connect + publikacja. Wycofanych r_basicprofile / r_emailaddress nie wolno tu dodawac -
      // LinkedIn odrzuca cala autoryzacje bledem unauthorized_scope_error.
      const scope = 'openid profile email w_member_social';

      // Store the current URL in localStorage for redirect after auth
      localStorage.setItem('linkedin_auth_redirect', window.location.href);

      const authUrl = new URL('https://www.linkedin.com/oauth/v2/authorization');
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('scope', scope);
      authUrl.searchParams.set('state', createOAuthState('linkedin'));

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('LinkedIn login error:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to connect LinkedIn account');
      setIsLoading(false);
    }
  };

  const handleProfileSelection = async () => {
    if (!selectedProfileId) {
      toast.error('Please select a profile or page');
      return;
    }

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const selectedProfile = profiles.find(p => p.id === selectedProfileId);
      if (!selectedProfile) throw new Error('Selected profile not found');

      const { error: dbError } = await supabase
        .from('connected_accounts')
        .insert({
          id: selectedProfile.id,
          name: selectedProfile.name,
          platform: 'linkedin',
          account_type: selectedProfile.type,
          user_id: user.id,
          access_token: localStorage.getItem('linkedin_access_token'),
        });

      if (dbError) throw dbError;

      setShowProfileSelector(false);
      onSuccess?.();
      toast.success('LinkedIn account connected successfully');
    } catch (error) {
      console.error('Error saving LinkedIn profile:', error);
      onError?.(error instanceof Error ? error.message : 'Failed to save LinkedIn profile');
    } finally {
      setIsLoading(false);
    }
  };

  if (showProfileSelector) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Select LinkedIn Profile or Page</h3>
        <div className="space-y-2">
          {profiles.map((profile) => (
            <label key={profile.id} className="flex items-center space-x-3">
              <input
                type="radio"
                checked={selectedProfileId === profile.id}
                onChange={() => setSelectedProfileId(profile.id)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <span className="text-sm font-medium text-gray-900">
                {profile.name} ({profile.type === 'person' ? 'Profile' : 'Page'})
              </span>
            </label>
          ))}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={() => setShowProfileSelector(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleProfileSelection}
            disabled={!selectedProfileId || isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isLoading ? 'Connecting...' : 'Connect'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <LinkedInSetupModal isOpen={showSetupModal} onClose={() => setShowSetupModal(false)} />

      <div className="space-y-2">
        <button
          onClick={handleLinkedInLogin}
          disabled={isLoading}
          className="flex items-center space-x-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg
            text-gray-500 hover:text-blue-700 hover:border-blue-700 w-full justify-center
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Linkedin className="h-5 w-5" />
              <span>{hasConnectedAccounts ? 'Connect Another LinkedIn Account' : 'Connect LinkedIn'}</span>
            </>
          )}
        </button>

        <button
          onClick={() => setShowSetupModal(true)}
          className="flex items-center justify-center space-x-2 w-full text-sm text-blue-600 hover:text-blue-700 py-1"
        >
          <HelpCircle className="w-4 h-4" />
          <span>How to set up LinkedIn integration?</span>
        </button>
      </div>
    </>
  );
}